import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import GlassCard from "@/components/common/GlassCard";
import MediaImageField from "@/components/common/MediaImageField";
import DragList from "@/components/common/DragList";
import PublicRouteLinkFields from "@/components/common/PublicRouteLinkFields";
import { resolveMediaUrl, siteStaticUrl } from "@/lib/serviceMedia";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import useUnsavedGuard from "@/hooks/useUnsavedGuard";

const PANELS = [
  { key: "brand", label: "Brand", icon: "Palette", blurb: "Logo & identity" },
  { key: "hero", label: "Hero", icon: "Sparkles", blurb: "First impression" },
  { key: "about", label: "About", icon: "Info", blurb: "Story copy" },
  { key: "services-header", label: "Services", icon: "Wrench", blurb: "Services heading" },
  { key: "section-headers", label: "Sections", icon: "LayoutTemplate", blurb: "Section titles" },
  { key: "cta", label: "CTA", icon: "Zap", blurb: "Contact form block" },
  { key: "pricing", label: "Pricing", icon: "CreditCard", blurb: "Packages & /pricing" },
  { key: "careers", label: "Careers", icon: "Briefcase", blurb: "Jobs & perks" },
  { key: "contact", label: "Contact", icon: "Mail", blurb: "Contact details" },
  { key: "footer", label: "Footer", icon: "PanelBottom", blurb: "Links & socials" },
];

// usesSubtitle: only sections whose public markup actually shows a subtitle.
// Most homepage blocks use label + title only (.section-header p is hidden in CSS).
const SECTION_HEADER_KEYS = [
  { key: "why_choose", label: "Why Choose Us", usesSubtitle: false },
  { key: "process", label: "Process Steps", usesSubtitle: false },
  { key: "challenges", label: "Challenges", usesSubtitle: false },
  { key: "industries", label: "Industries", usesSubtitle: false },
  { key: "stats", label: "Stats", usesSubtitle: false },
  { key: "logos", label: "Client Logos", usesSubtitle: false },
  { key: "testimonials", label: "Testimonials", usesSubtitle: false },
  { key: "team", label: "Team", usesSubtitle: false },
  { key: "faq", label: "FAQ", usesSubtitle: false },
  { key: "careers", label: "Careers", usesSubtitle: false },
  { key: "portfolio", label: "Portfolio", usesSubtitle: false },
  { key: "pricing", label: "Pricing", usesSubtitle: true },
  { key: "blog", label: "Blog", usesSubtitle: false },
  { key: "contact_page", label: "Contact Page", usesSubtitle: true },
];

function useSiteSection(endpoint) {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/admin/site/${endpoint}`);
      setData(d || {});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [endpoint]); // eslint-disable-line
  const { dirty, markClean } = useUnsavedGuard(data, { ready: !loading });
  return { data, setData, loading, dirty, markClean };
}

const Field = ({ label, children, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-[10px] uppercase tracking-[0.14em] text-white/45">{label}</Label>
    {children}
    {hint && <p className="text-[11px] text-white/35 leading-relaxed">{hint}</p>}
  </div>
);

const inputClass = "bg-black/25 border-white/10 text-white placeholder:text-white/25 focus-visible:ring-cyan-400/30";

function isEnabled(value) {
  return value !== false;
}

/** Careers-style page/section visibility toggle for Brand & Pages panels. */
function EnabledToggle({
  checked,
  onChange,
  label = "Page enabled",
  onHint = "Public page/section is live.",
  offHint = "Hidden on the public site — turn on to publish.",
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-black/20 px-3 py-2.5">
      <div>
        <span className="text-xs text-white/70">{label}</span>
        <p className="text-[11px] text-white/40 mt-0.5">{checked ? onHint : offHint}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

function EnabledBadge({ enabled }) {
  return (
    <div className={`text-[11px] font-semibold mb-3 ${enabled ? "text-emerald-300/90" : "text-white/40"}`}>
      {enabled ? "● Page enabled" : "○ Page disabled"}
    </div>
  );
}

function StudioShell({
  title, description, dirty, saving, onSave, preview, children, loading,
}) {
  const [fullView, setFullView] = useState(false);

  if (loading) {
    return <div className="text-sm text-white/40 py-16 text-center">Loading…</div>;
  }
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-white tracking-tight">{title}</h2>
          <p className="text-xs text-white/45 mt-0.5 max-w-xl">{description}</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="text-white/70 hover:text-white h-9 px-2.5 border border-white/15 bg-white/[0.04] hover:bg-white/10"
            onClick={() => setFullView((v) => !v)}
            title={fullView ? "Collapse" : "Expand"}
            aria-pressed={fullView}
          >
            {fullView ? (
              <><Icons.Minimize2 className="w-3.5 h-3.5 mr-1.5" /> Collapse</>
            ) : (
              <><Icons.Maximize2 className="w-3.5 h-3.5 mr-1.5" /> Expand</>
            )}
          </Button>
          <span className={`text-xs ${dirty ? "text-amber-300" : "text-emerald-300/80"}`}>
            {dirty ? "Unsaved changes" : "All changes saved"}
          </span>
          <Button onClick={onSave} disabled={saving || !dirty} className="btn-gradient text-white border-0 h-9">
            <Icons.Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      <div className={fullView ? "space-y-4" : "grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-4 items-start"}>
        {!fullView && (
          <GlassCard className="p-5 min-h-[360px] relative overflow-hidden">
            <div className="absolute inset-0 pointer-events-none opacity-40" style={{
              background: "radial-gradient(ellipse at top, rgba(34,211,238,0.08), transparent 55%)",
            }} />
            <div className="relative">
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/35 mb-4">Live preview</div>
              {preview}
            </div>
          </GlassCard>
        )}

        <GlassCard
          className={`p-4 sm:p-6 space-y-4 h-fit w-full ${
            fullView
              ? "max-w-[1600px] max-h-[calc(100vh-160px)] overflow-y-auto"
              : "xl:sticky xl:top-20 max-h-[calc(100vh-8rem)] overflow-y-auto"
          }`}
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/35">
              {fullView ? `Edit ${title}` : "Inspector"}
            </div>
            {fullView && (
              <p className="text-[11px] text-white/35">Expanded editor — Collapse to show live preview again.</p>
            )}
          </div>
          <div className={fullView ? "grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4 content-start" : "space-y-4"}>
            {React.Children.map(children, (child, idx) => {
              if (!child) return null;
              if (!fullView) return child;
              const label = child?.props?.label || "";
              const span =
                /packages|columns|openings|perks|social|contact rows|service options|body|subtitle|intro|description|side image|footer logo|logo|favicon|mark/i.test(label);
              return (
                <div key={child.key || idx} className={span ? "lg:col-span-2" : undefined}>
                  {child}
                </div>
              );
            })}
          </div>
          {fullView && preview ? (
            <div className="pt-4 mt-2 border-t border-white/10">
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/35 mb-3">Live preview</div>
              <div className="rounded-xl border border-white/10 overflow-hidden bg-black/20 p-4">
                {preview}
              </div>
            </div>
          ) : null}
        </GlassCard>
      </div>
    </div>
  );
}

/* ── Brand ── */
function BrandPanel() {
  const { data, setData, loading, dirty, markClean } = useSiteSection("brand");
  const [saving, setSaving] = useState(false);
  const brandFallback = siteStaticUrl("brandLogo");
  const faviconFallback = siteStaticUrl("favicon");
  const logoPreview = resolveMediaUrl(data.logo_url, brandFallback) || brandFallback;
  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/site/brand", data);
      markClean(data);
      toast.success("Brand saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving(false);
  };

  return (
    <StudioShell
      title="Brand identity"
      description="Logo, wordmark, and mark shown in the site navigation, footer, and browser tab. Saving brand logo also updates the public footer logo."
      dirty={dirty} saving={saving} onSave={save} loading={loading}
      preview={(
        <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.07] to-transparent p-8">
          <div className="flex items-center gap-4">
            {logoPreview ? (
              <img src={logoPreview} alt="" className="w-14 h-14 object-contain rounded-xl bg-white p-1.5 shadow-lg" />
            ) : (
              <div className="w-14 h-14 rounded-xl btn-gradient flex items-center justify-center text-white font-bold text-lg shadow-lg">
                {data.mark || data.name?.slice(0, 2) || "CM"}
              </div>
            )}
            <div>
              {(data.showText !== false || data.is_wordmark) && (
                <div className="text-xl font-semibold text-white tracking-tight">
                  {data.name || "Brand name"}{" "}
                  <span className="text-cyan-200/90">{data.suffix || ""}</span>
                </div>
              )}
              <p className="text-xs text-white/40 mt-1">
                {data.is_wordmark ? "Wordmark mode" : "Logo + text"} · Favicon {data.favicon_url || faviconFallback ? "set" : "missing"}
              </p>
            </div>
          </div>
        </div>
      )}
    >
      <Field label="Brand name"><Input value={data.name || ""} onChange={(e) => setData({ ...data, name: e.target.value })} className={inputClass} /></Field>
      <Field label="Suffix"><Input value={data.suffix || ""} onChange={(e) => setData({ ...data, suffix: e.target.value })} placeholder="Solutions" className={inputClass} /></Field>
      <Field label="Logo mark" hint="Fallback letters when no logo image"><Input value={data.mark || ""} onChange={(e) => setData({ ...data, mark: e.target.value })} placeholder="CM" className={inputClass} /></Field>
      <MediaImageField label="Logo" value={data.logo_url || ""} onChange={(url) => setData({ ...data, logo_url: url })} folder="brand" aspectClass="h-24" fallbackSrc={brandFallback} />
      <MediaImageField label="Favicon" value={data.favicon_url || ""} onChange={(url) => setData({ ...data, favicon_url: url })} folder="brand" aspectClass="h-20" fallbackSrc={faviconFallback} />
      <div className="flex items-center justify-between gap-3 py-1">
        <span className="text-xs text-white/60">Wordmark (text logo)</span>
        <Switch checked={!!data.is_wordmark} onCheckedChange={(v) => setData({ ...data, is_wordmark: v })} />
      </div>
      <div className="flex items-center justify-between gap-3 py-1">
        <span className="text-xs text-white/60">Show brand text with logo</span>
        <Switch checked={data.showText !== false} onCheckedChange={(v) => setData({ ...data, showText: v })} />
      </div>
    </StudioShell>
  );
}

/* ── Hero ── */
function HeroPanel() {
  const { data, setData, loading, dirty, markClean } = useSiteSection("hero");
  const [saving, setSaving] = useState(false);
  const heroFallback = siteStaticUrl("hero");
  const heroPreview = resolveMediaUrl(data.media?.src, heroFallback) || heroFallback;

  const tags = (data.subheading_items && data.subheading_items.length)
    ? [...data.subheading_items]
    : (data.subheading || "").split("\u2022").map((s) => s.trim()).filter(Boolean);

  const setTags = (items) => {
    setData({
      ...data,
      subheading_items: items,
      subheading: items.filter(Boolean).join(" \u2022 "),
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/site/hero", data);
      markClean(data);
      toast.success("Hero saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving(false);
  };

  return (
    <StudioShell
      title="Hero section"
      description="Background, headline, service tags, CTAs, and portfolio dropdown. Background uses the same template image as the public homepage."
      dirty={dirty} saving={saving} onSave={save} loading={loading}
      preview={(
        <div>
          <EnabledBadge enabled={isEnabled(data.enabled)} />
          <div className="rounded-2xl overflow-hidden border border-white/10 relative min-h-[280px]">
            {heroPreview ? (
              <img src={heroPreview} alt="" className="absolute inset-0 w-full h-full object-cover" />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-950" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/45 to-black/25" />
            <div className="relative p-8 flex flex-col justify-end min-h-[280px]">
              <h3 className="text-2xl sm:text-3xl font-semibold text-white tracking-tight max-w-lg">
                {data.title_lead || "Headline"}{" "}
                <span className="text-cyan-200">{data.title_accent || ""}</span>
              </h3>
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {tags.map((t, i) => (
                    <span key={i} className="px-2.5 py-1 rounded-full text-[11px] bg-white/15 text-white/90 border border-white/20 backdrop-blur-sm">{t}</span>
                  ))}
                </div>
              )}
              {data.subtitle && <p className="text-sm text-white/70 mt-3 max-w-md line-clamp-2">{data.subtitle}</p>}
              <div className="flex flex-wrap gap-2 mt-4">
                <span className="inline-flex px-3 py-1.5 rounded-md btn-gradient text-xs text-white">{data.primary_cta?.label || "Primary CTA"}</span>
                <span className="inline-flex px-3 py-1.5 rounded-md border border-white/25 text-xs text-white/85">{data.secondary_cta?.label || "Secondary"}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    >
      <EnabledToggle
        checked={isEnabled(data.enabled)}
        onChange={(v) => setData({ ...data, enabled: v })}
        label="Hero section enabled"
        onHint="Hero shows on the public homepage."
        offHint="Hero is hidden on the homepage."
      />
      <MediaImageField label="Background" value={data.media || null} onChange={(media) => setData({ ...data, media })} folder="hero" valueMode="media" aspectClass="h-32" fallbackSrc={heroFallback} />
      <Field label="Title lead"><Input value={data.title_lead || ""} onChange={(e) => setData({ ...data, title_lead: e.target.value })} className={inputClass} /></Field>
      <Field label="Title accent"><Input value={data.title_accent || ""} onChange={(e) => setData({ ...data, title_accent: e.target.value })} className={inputClass} /></Field>
      <Field label="Service tags" hint="Drag to reorder · shown as pills under the headline">
        <DragList
          items={tags}
          onChange={setTags}
          emptyText="No tags yet"
          addLabel="Add tag"
          onAdd={() => setTags([...tags, "New tag"])}
          renderItem={(item, _i, { updateValue }) => (
            <Input value={item} onChange={(e) => updateValue(e.target.value)} className={`${inputClass} text-xs h-8`} />
          )}
        />
      </Field>
      <Field label="Subtitle"><Textarea value={data.subtitle || ""} onChange={(e) => setData({ ...data, subtitle: e.target.value })} rows={2} className={inputClass} /></Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Primary CTA"><Input value={data.primary_cta?.label || ""} onChange={(e) => setData({ ...data, primary_cta: { ...data.primary_cta, label: e.target.value } })} className={inputClass} /></Field>
        <Field label="Primary link"><Input value={data.primary_cta?.href || ""} onChange={(e) => setData({ ...data, primary_cta: { ...data.primary_cta, href: e.target.value } })} className={inputClass} /></Field>
        <Field label="Secondary CTA"><Input value={data.secondary_cta?.label || ""} onChange={(e) => setData({ ...data, secondary_cta: { ...data.secondary_cta, label: e.target.value } })} className={inputClass} /></Field>
        <Field label="Secondary link"><Input value={data.secondary_cta?.href || ""} onChange={(e) => setData({ ...data, secondary_cta: { ...data.secondary_cta, href: e.target.value } })} className={inputClass} /></Field>
      </div>
      <Field label="Portfolio dropdown" hint="Drag to reorder links in the View Portfolio menu">
        <DragList
          items={data.portfolio_links || []}
          onChange={(portfolio_links) => setData({ ...data, portfolio_links })}
          emptyText="No portfolio links"
          addLabel="Add link"
          onAdd={() => setData({ ...data, portfolio_links: [...(data.portfolio_links || []), { label: "", href: "" }] })}
          renderItem={(link, _i, { update }) => (
            <div className="space-y-1.5">
              <Input value={link.label || ""} onChange={(e) => update({ label: e.target.value })} placeholder="Label" className={`${inputClass} text-xs h-8`} />
              <Input value={link.href || ""} onChange={(e) => update({ href: e.target.value })} placeholder="https://… or /media/…" className={`${inputClass} text-xs h-8`} />
            </div>
          )}
        />
      </Field>
    </StudioShell>
  );
}

/* ── About ── */
function AboutPanel() {
  const { data, setData, loading, dirty, markClean } = useSiteSection("about");
  const [saving, setSaving] = useState(false);
  const paragraphs = Array.isArray(data.body) ? data.body : [];
  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/site/about", data);
      markClean(data);
      toast.success("About saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving(false);
  };

  return (
    <StudioShell
      title="About section"
      description="Label, heading, and body paragraphs. Drag paragraphs to change reading order."
      dirty={dirty} saving={saving} onSave={save} loading={loading}
      preview={(
        <div className="space-y-3 max-w-xl">
          <EnabledBadge enabled={isEnabled(data.enabled)} />
          <span className="text-[10px] uppercase tracking-[0.16em] text-cyan-200/80">{data.label || "About"}</span>
          <h3 className="text-2xl font-semibold text-white tracking-tight">{data.title || "About title"}</h3>
          <div className="space-y-2">
            {paragraphs.map((p, i) => <p key={i} className="text-sm text-white/65 leading-relaxed">{p}</p>)}
            {paragraphs.length === 0 && <p className="text-sm text-white/35">Add body paragraphs in the inspector.</p>}
          </div>
          {data.focus && <p className="text-sm text-cyan-100/90 font-medium pt-2 border-t border-white/10">{data.focus}</p>}
        </div>
      )}
    >
      <EnabledToggle
        checked={isEnabled(data.enabled)}
        onChange={(v) => setData({ ...data, enabled: v })}
        label="About section enabled"
        onHint="About block shows on the public homepage."
        offHint="About is hidden on the homepage."
      />
      <Field label="Section label"><Input value={data.label || ""} onChange={(e) => setData({ ...data, label: e.target.value })} className={inputClass} /></Field>
      <Field label="Title"><Input value={data.title || ""} onChange={(e) => setData({ ...data, title: e.target.value })} className={inputClass} /></Field>
      <Field label="Body paragraphs" hint="Drag to reorder">
        <DragList
          items={paragraphs}
          onChange={(body) => setData({ ...data, body })}
          emptyText="No paragraphs"
          addLabel="Add paragraph"
          onAdd={() => setData({ ...data, body: [...paragraphs, ""] })}
          renderItem={(item, _i, { updateValue }) => (
            <Textarea value={item} onChange={(e) => updateValue(e.target.value)} rows={2} className={`${inputClass} text-xs`} />
          )}
        />
      </Field>
      <Field label="Focus line"><Input value={data.focus || ""} onChange={(e) => setData({ ...data, focus: e.target.value })} className={inputClass} /></Field>
    </StudioShell>
  );
}

/* ── Services header ── */
function ServicesHeaderPanel() {
  const { data, setData, loading, dirty, markClean } = useSiteSection("services-header");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/site/services-header", data);
      markClean(data);
      toast.success("Services header saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving(false);
  };
  return (
    <StudioShell
      title="Services header"
      description="Label and title above the services carousel. The public layout does not show a subtitle under this heading."
      dirty={dirty} saving={saving} onSave={save} loading={loading}
      preview={(
        <div className="text-center space-y-2 py-10">
          <EnabledBadge enabled={isEnabled(data.enabled)} />
          <span className="text-[10px] uppercase tracking-[0.16em] text-cyan-200/80">{data.label || "Services"}</span>
          <h3 className="text-2xl font-semibold text-white">{data.title || "Services title"}</h3>
        </div>
      )}
    >
      <EnabledToggle
        checked={isEnabled(data.enabled)}
        onChange={(v) => setData({ ...data, enabled: v })}
        label="Services section enabled"
        onHint="Services block shows on the public homepage."
        offHint="Services are hidden on the homepage."
      />
      <Field label="Label"><Input value={data.label || ""} onChange={(e) => setData({ ...data, label: e.target.value })} className={inputClass} /></Field>
      <Field label="Title"><Input value={data.title || ""} onChange={(e) => setData({ ...data, title: e.target.value })} className={inputClass} /></Field>
    </StudioShell>
  );
}

/* ── Section headers ── */
function SectionHeadersPanel({ initialSection }) {
  const { data, setData, loading, dirty, markClean } = useSiteSection("sections");
  const [saving, setSaving] = useState(false);
  const valid = SECTION_HEADER_KEYS.some((s) => s.key === initialSection);
  const [selected, setSelected] = useState(valid ? initialSection : "why_choose");
  useEffect(() => {
    if (valid) setSelected(initialSection);
  }, [initialSection, valid]);
  const headers = data.headers || {};
  const current = headers[selected] || {};

  const setHeader = (field, value) => {
    setData({
      ...data,
      headers: { ...headers, [selected]: { ...current, [field]: value } },
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/site/sections", data);
      markClean(data);
      toast.success("Section headers saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving(false);
  };

  const meta = SECTION_HEADER_KEYS.find((s) => s.key === selected);
  const usesSubtitle = !!meta?.usesSubtitle;

  return (
    <StudioShell
      title="Section headings"
      description="Label and title for each public section. Subtitle is only editable where the live page actually shows it (Contact Page)."
      dirty={dirty} saving={saving} onSave={save} loading={loading}
      preview={(
        <div className="space-y-3">
          <div className="flex flex-wrap gap-1.5">
            {SECTION_HEADER_KEYS.map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setSelected(s.key)}
                className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${
                  selected === s.key
                    ? "bg-cyan-400/15 border-cyan-400/40 text-cyan-100"
                    : "border-white/10 text-white/50 hover:border-white/25 hover:text-white/80"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <div className="rounded-2xl border border-white/10 px-6 py-10 text-center bg-gradient-to-b from-white/[0.05] to-transparent">
            <EnabledBadge enabled={isEnabled(current.enabled)} />
            <span className="text-[11px] uppercase tracking-[0.18em] text-cyan-200/85 font-semibold">
              {current.label || meta?.label}
            </span>
            <h3 className="text-2xl md:text-3xl font-semibold text-white mt-3 tracking-tight">
              {current.title || `${meta?.label} heading`}
            </h3>
            {usesSubtitle ? (
              <p className="text-sm text-white/55 mt-3 max-w-lg mx-auto leading-relaxed">
                {current.subtitle || "Supporting description for this section."}
              </p>
            ) : (
              <p className="text-[11px] text-white/35 mt-4">
                Public UI shows label + title only for this section.
              </p>
            )}
          </div>
          <p className="text-[11px] text-white/35 text-center">
            Public page uses this heading block for <span className="text-white/60">{meta?.label || selected}</span>.
          </p>
        </div>
      )}
    >
      <Field label="Editing">
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          className={`w-full rounded-md px-3 py-2 text-sm ${inputClass}`}
        >
          {SECTION_HEADER_KEYS.map((s) => (
            <option key={s.key} value={s.key} className="bg-slate-900">{s.label}</option>
          ))}
        </select>
      </Field>
      <EnabledToggle
        checked={isEnabled(current.enabled)}
        onChange={(v) => setHeader("enabled", v)}
        label={`${meta?.label || "Section"} enabled`}
        onHint="This section can show on the public homepage."
        offHint="This section is hidden on the public homepage."
      />
      <Field label="Section label"><Input value={current.label || ""} onChange={(e) => setHeader("label", e.target.value)} className={inputClass} /></Field>
      <Field label="Title"><Input value={current.title || ""} onChange={(e) => setHeader("title", e.target.value)} className={inputClass} /></Field>
      {usesSubtitle ? (
        <Field label="Subtitle" hint="Shown on the public Contact page hero and form intro.">
          <Textarea value={current.subtitle || ""} onChange={(e) => setHeader("subtitle", e.target.value)} rows={3} className={inputClass} />
        </Field>
      ) : null}
    </StudioShell>
  );
}

/* ── CTA ── */
function CtaPanel() {
  const { data, setData, loading, dirty, markClean } = useSiteSection("cta");
  const [saving, setSaving] = useState(false);
  const options = data.service_options || [];
  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/site/cta", data);
      markClean(data);
      toast.success("CTA saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving(false);
  };

  return (
    <StudioShell
      title="CTA / contact block"
      description="Badge, title, subtitle, submit label, service options, and optional side image — same fields as the public homepage CTA form."
      dirty={dirty} saving={saving} onSave={save} loading={loading}
      preview={(
        <div>
          <EnabledBadge enabled={isEnabled(data.enabled)} />
          <div
            className="rounded-2xl overflow-hidden border border-white/10"
            style={{ background: "linear-gradient(160deg, #071628 0%, #0a2540 60%, #123a63 100%)" }}
          >
            <div className="grid sm:grid-cols-2 gap-0">
              <div className="p-6 space-y-3">
                <span className="text-[10px] uppercase tracking-[0.16em] text-cyan-200/80 font-semibold">{data.label || "Get Started"}</span>
                <h3 className="text-xl font-semibold text-white leading-snug">{data.title || "CTA title"}</h3>
                <p className="text-sm text-white/60 leading-relaxed line-clamp-4">{data.subtitle || "Supporting copy"}</p>
                <ul className="text-[11px] text-white/45 space-y-1 pt-1">
                  <li>• Free consultation</li>
                  <li>• Clear project roadmap</li>
                  <li>• Fast response</li>
                </ul>
                {data.media?.src ? (
                  <img
                    src={resolveMediaUrl(data.media.src)}
                    alt=""
                    className="mt-3 w-full max-h-28 object-cover rounded-lg border border-white/10"
                  />
                ) : null}
              </div>
              <div className="p-6 bg-black/25 border-l border-white/10 space-y-2.5">
                <div className="h-8 rounded-md bg-white/5 border border-white/10" />
                <div className="h-8 rounded-md bg-white/5 border border-white/10" />
                <div className="h-8 rounded-md bg-white/5 border border-white/10 flex items-center px-3 text-[11px] text-white/45">
                  {options[0] || "Select service"} ▾
                </div>
                <div className="h-16 rounded-md bg-white/5 border border-white/10" />
                <div className="inline-flex px-3 py-2 rounded-md btn-gradient text-xs text-white">
                  {data.submit_label || "Submit"}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    >
      <EnabledToggle
        checked={isEnabled(data.enabled)}
        onChange={(v) => setData({ ...data, enabled: v })}
        label="CTA section enabled"
        onHint="CTA / consultation form shows on the homepage."
        offHint="CTA block is hidden on the homepage."
      />
      <Field label="Badge"><Input value={data.label || ""} onChange={(e) => setData({ ...data, label: e.target.value })} className={inputClass} /></Field>
      <Field label="Title"><Input value={data.title || ""} onChange={(e) => setData({ ...data, title: e.target.value })} className={inputClass} /></Field>
      <Field label="Subtitle"><Textarea value={data.subtitle || ""} onChange={(e) => setData({ ...data, subtitle: e.target.value })} rows={3} className={inputClass} /></Field>
      <Field label="Submit button"><Input value={data.submit_label || ""} onChange={(e) => setData({ ...data, submit_label: e.target.value })} className={inputClass} /></Field>
      <MediaImageField label="Side image" value={data.media || null} onChange={(media) => setData({ ...data, media })} folder="cta" valueMode="media" aspectClass="h-28" />
      <Field label="Service options" hint="Drag to reorder dropdown choices">
        <DragList
          items={options}
          onChange={(service_options) => setData({ ...data, service_options })}
          emptyText="No options"
          addLabel="Add option"
          onAdd={() => setData({ ...data, service_options: [...options, "New service"] })}
          renderItem={(item, _i, { updateValue }) => (
            <Input value={item} onChange={(e) => updateValue(e.target.value)} className={`${inputClass} text-xs h-8`} />
          )}
        />
      </Field>
    </StudioShell>
  );
}

/* ── Pricing ── */
function PricingPanel() {
  const [data, setData] = useState({ label: "", title: "", subtitle: "", enabled: true, packages: [] });
  const [sectionsDoc, setSectionsDoc] = useState(null);
  const [baselineIds, setBaselineIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { dirty, markClean } = useUnsavedGuard(data, { ready: !loading });

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: sections }, { data: pricing }] = await Promise.all([
        api.get("/admin/site/sections"),
        api.get("/admin/site/pricing?page_size=100"),
      ]);
      const headers = sections?.headers || {};
      const ph = headers.pricing || {};
      const packages = (pricing?.items || [])
        .slice()
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((p) => ({
          id: p.id,
          name: p.name || "",
          description: p.description || p.desc || "",
          features: Array.isArray(p.features) ? p.features : (Array.isArray(p.includes) ? p.includes : []),
          cta_label: p.cta_label || p.cta || "Get Started",
          featured: !!p.featured,
          status: p.status || "published",
        }));
      const next = {
        label: ph.label || "",
        title: ph.title || "",
        subtitle: ph.subtitle || "",
        enabled: ph.enabled !== false,
        packages,
      };
      setSectionsDoc(sections || { headers: {} });
      setBaselineIds(packages.map((p) => p.id).filter(Boolean));
      setData(next);
      markClean(next);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      const headers = { ...(sectionsDoc?.headers || {}) };
      headers.pricing = {
        label: (data.label || "").trim(),
        title: (data.title || "").trim(),
        subtitle: (data.subtitle || "").trim(),
        enabled: data.enabled !== false,
      };
      await api.patch("/admin/site/sections", { ...(sectionsDoc || {}), headers });

      const keepIds = new Set();
      for (let i = 0; i < (data.packages || []).length; i += 1) {
        const pkg = data.packages[i];
        const payload = {
          name: (pkg.name || "").trim(),
          description: (pkg.description || "").trim(),
          features: Array.isArray(pkg.features)
            ? pkg.features.map((f) => String(f).trim()).filter(Boolean)
            : String(pkg.features || "")
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean),
          cta_label: (pkg.cta_label || "Get Started").trim(),
          featured: !!pkg.featured,
          status: pkg.status === "draft" ? "draft" : "published",
          order: i + 1,
        };
        if (pkg.id) {
          await api.patch(`/admin/site/pricing/${pkg.id}`, payload);
          keepIds.add(pkg.id);
        } else {
          const { data: created } = await api.post("/admin/site/pricing", payload);
          if (created?.id) keepIds.add(created.id);
        }
      }
      for (const id of baselineIds) {
        if (id && !keepIds.has(id)) {
          await api.delete(`/admin/site/pricing/${id}`);
        }
      }
      toast.success("Pricing saved");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving(false);
  };

  const packages = data.packages || [];

  return (
    <StudioShell
      title="Pricing page"
      description="Page hero copy for /pricing plus quote packages shown on the pricing page and homepage. Draft packages stay off the live site."
      dirty={dirty} saving={saving} onSave={save} loading={loading}
      preview={(
        <div className="space-y-5">
          <EnabledBadge enabled={isEnabled(data.enabled)} />
          <div className="rounded-2xl border border-white/10 px-5 py-8 bg-gradient-to-br from-cyan-400/10 to-transparent">
            <span className="text-[10px] uppercase tracking-[0.16em] text-cyan-200/80 font-semibold">{data.label || "Pricing"}</span>
            <h3 className="text-xl font-semibold text-white mt-2 leading-snug">{data.title || "Pricing title"}</h3>
            <p className="text-sm text-white/55 mt-2 leading-relaxed line-clamp-3">{data.subtitle || "Page intro"}</p>
          </div>
          <div className="grid gap-2">
            {packages.filter((p) => p.status !== "draft").slice(0, 3).map((p, i) => (
              <div key={p.id || i} className={`rounded-xl border px-4 py-3 ${p.featured ? "border-amber-400/40 bg-amber-400/5" : "border-white/10"}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm text-white font-medium">{p.name || "Package"}</div>
                  {p.featured ? <span className="text-[10px] text-amber-300">Featured</span> : null}
                </div>
                <p className="text-[11px] text-white/45 mt-1 line-clamp-2">{p.description}</p>
                <div className="text-[10px] text-cyan-200/70 mt-2">{p.cta_label || "Get Started"}</div>
              </div>
            ))}
            {packages.length === 0 && <p className="text-xs text-white/40 text-center py-4">No packages yet</p>}
          </div>
        </div>
      )}
    >
      <EnabledToggle
        checked={isEnabled(data.enabled)}
        onChange={(v) => setData({ ...data, enabled: v })}
        label="Pricing page enabled"
        onHint="Public /pricing page and homepage pricing block are live."
        offHint="Pricing is hidden on the homepage; /pricing shows a closed message."
      />
      <Field label="Page label"><Input value={data.label || ""} onChange={(e) => setData({ ...data, label: e.target.value })} className={inputClass} placeholder="Pricing" /></Field>
      <Field label="Page title"><Input value={data.title || ""} onChange={(e) => setData({ ...data, title: e.target.value })} className={inputClass} /></Field>
      <Field label="Page intro" hint="Shown in the /pricing page hero.">
        <Textarea value={data.subtitle || ""} onChange={(e) => setData({ ...data, subtitle: e.target.value })} rows={3} className={inputClass} />
      </Field>
      <Field label="Packages" hint="Drag to reorder. Features are comma-separated.">
        <DragList
          items={packages}
          onChange={(next) => setData({ ...data, packages: next })}
          emptyText="No packages"
          addLabel="Add package"
          getKey={(p, i) => p.id || `new-${i}`}
          onAdd={() => setData({
            ...data,
            packages: [...packages, {
              name: "New package",
              description: "",
              features: [],
              cta_label: "Request Quote",
              featured: false,
              status: "published",
            }],
          })}
          renderItem={(p, _i, { update }) => (
            <div className="space-y-1.5">
              <div className="grid grid-cols-[1fr_auto] gap-1.5">
                <Input value={p.name || ""} onChange={(e) => update({ name: e.target.value })} placeholder="Package name" className={`${inputClass} text-xs h-8`} />
                <select
                  value={p.status || "published"}
                  onChange={(e) => update({ status: e.target.value })}
                  className={`${inputClass} text-xs h-8 rounded-md px-2`}
                >
                  <option value="published" className="bg-slate-900">Published</option>
                  <option value="draft" className="bg-slate-900">Draft</option>
                </select>
              </div>
              <Textarea value={p.description || ""} onChange={(e) => update({ description: e.target.value })} placeholder="Description" rows={2} className={`${inputClass} text-xs`} />
              <Input
                value={Array.isArray(p.features) ? p.features.join(", ") : (p.features || "")}
                onChange={(e) => update({
                  features: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                })}
                placeholder="Features (comma-separated)"
                className={`${inputClass} text-xs h-8`}
              />
              <div className="grid grid-cols-[1fr_auto] gap-1.5 items-center">
                <Input value={p.cta_label || ""} onChange={(e) => update({ cta_label: e.target.value })} placeholder="CTA label" className={`${inputClass} text-xs h-8`} />
                <label className="flex items-center gap-1.5 text-[11px] text-white/60 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={!!p.featured}
                    onChange={(e) => update({ featured: e.target.checked })}
                    className="rounded border-white/20"
                  />
                  Featured
                </label>
              </div>
            </div>
          )}
        />
      </Field>
    </StudioShell>
  );
}

/* ── Careers ── */
function CareersPanel() {
  const { data, setData, loading, dirty, markClean } = useSiteSection("careers");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/site/careers", data);
      markClean(data);
      toast.success("Careers saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving(false);
  };

  return (
    <StudioShell
      title="Careers"
      description="When this page is disabled, /careers shows a short ‘no openings’ message and the careers block is hidden on the homepage. Turn it on to publish openings and perks."
      dirty={dirty} saving={saving} onSave={save} loading={loading}
      preview={(
        <div className="space-y-4">
          <EnabledBadge enabled={isEnabled(data.enabled)} />
          <h3 className="text-2xl font-semibold text-white">{data.headline || "Join the team"}</h3>
          <div className="space-y-2">
            {(data.openings || []).slice(0, 3).map((o, i) => (
              <div key={i} className="rounded-xl border border-white/10 px-4 py-3 flex justify-between gap-3">
                <div>
                  <div className="text-sm text-white font-medium">{o.title || "Role"}</div>
                  <div className="text-[11px] text-white/45">{[o.dept, o.type, o.location].filter(Boolean).join(" · ")}</div>
                </div>
              </div>
            ))}
          </div>
          {(data.perks || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-2">
              {(data.perks || []).slice(0, 6).map((p, i) => (
                <span key={i} className="px-2 py-1 rounded-full text-[11px] border border-white/15 text-white/70">{p}</span>
              ))}
            </div>
          )}
        </div>
      )}
    >
      <EnabledToggle
        checked={isEnabled(data.enabled)}
        onChange={(v) => setData({ ...data, enabled: v })}
        label="Careers page enabled"
        onHint="Public /careers page is live."
        offHint="Public /careers is hidden behind the closed message — turn on to show openings."
      />
      <Field label="Headline"><Input value={data.headline || ""} onChange={(e) => setData({ ...data, headline: e.target.value })} className={inputClass} /></Field>
      <Field label="CTA label"><Input value={data.cta?.label || ""} onChange={(e) => setData({ ...data, cta: { ...data.cta, label: e.target.value } })} className={inputClass} /></Field>
      <Field label="Job openings" hint="Drag to reorder">
        <DragList
          items={data.openings || []}
          onChange={(openings) => setData({ ...data, openings })}
          emptyText="No openings"
          addLabel="Add opening"
          onAdd={() => setData({ ...data, openings: [...(data.openings || []), { title: "", dept: "", type: "", location: "" }] })}
          renderItem={(o, _i, { update }) => (
            <div className="grid grid-cols-2 gap-1.5">
              <Input value={o.title || ""} onChange={(e) => update({ title: e.target.value })} placeholder="Title" className={`${inputClass} text-xs h-8`} />
              <Input value={o.dept || ""} onChange={(e) => update({ dept: e.target.value })} placeholder="Dept" className={`${inputClass} text-xs h-8`} />
              <Input value={o.type || ""} onChange={(e) => update({ type: e.target.value })} placeholder="Type" className={`${inputClass} text-xs h-8`} />
              <Input value={o.location || ""} onChange={(e) => update({ location: e.target.value })} placeholder="Location" className={`${inputClass} text-xs h-8`} />
            </div>
          )}
        />
      </Field>
      <Field label="Perks" hint="Drag to reorder">
        <DragList
          items={data.perks || []}
          onChange={(perks) => setData({ ...data, perks })}
          emptyText="No perks"
          addLabel="Add perk"
          onAdd={() => setData({ ...data, perks: [...(data.perks || []), ""] })}
          renderItem={(p, _i, { updateValue }) => (
            <Input value={p} onChange={(e) => updateValue(e.target.value)} className={`${inputClass} text-xs h-8`} />
          )}
        />
      </Field>
    </StudioShell>
  );
}

/* ── Contact ── */
function ContactPanel() {
  const { data, setData, loading, dirty, markClean } = useSiteSection("contact");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/site/contact", data);
      markClean(data);
      toast.success("Contact saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving(false);
  };
  return (
    <StudioShell
      title="Contact details"
      description="Public location, email, phone, and website. Saving here also syncs the Contact column in the public footer."
      dirty={dirty} saving={saving} onSave={save} loading={loading}
      preview={(
        <div className="rounded-2xl border border-white/10 p-6 space-y-3 bg-white/[0.03]">
          <EnabledBadge enabled={isEnabled(data.enabled)} />
          {[
            { icon: Icons.MapPin, v: data.location },
            { icon: Icons.Mail, v: data.email },
            { icon: Icons.Phone, v: data.phone },
            { icon: Icons.Globe, v: data.website },
          ].map(({ icon: I, v }, i) => (
            <div key={i} className="flex items-center gap-3 text-sm text-white/70">
              <I className="w-4 h-4 text-cyan-200/70 shrink-0" />
              <span>{v || "—"}</span>
            </div>
          ))}
        </div>
      )}
    >
      <EnabledToggle
        checked={isEnabled(data.enabled)}
        onChange={(v) => setData({ ...data, enabled: v })}
        label="Contact page enabled"
        onHint="Public /contact page and homepage contact preview are live."
        offHint="Contact is hidden on the homepage; /contact shows a closed message."
      />
      <Field label="Location"><Input value={data.location || ""} onChange={(e) => setData({ ...data, location: e.target.value })} className={inputClass} /></Field>
      <Field label="Email"><Input value={data.email || ""} onChange={(e) => setData({ ...data, email: e.target.value })} className={inputClass} /></Field>
      <Field label="Phone"><Input value={data.phone || ""} onChange={(e) => setData({ ...data, phone: e.target.value })} className={inputClass} /></Field>
      <Field label="Website"><Input value={data.website || ""} onChange={(e) => setData({ ...data, website: e.target.value })} className={inputClass} /></Field>
    </StudioShell>
  );
}

/* ── Footer ── */
const CONTACT_TYPE_OPTIONS = [
  { value: "MAIL", label: "Email" },
  { value: "CALL", label: "Phone" },
  { value: "LOC", label: "Office" },
  { value: "WEB", label: "Website" },
];

const FOOTER_COLUMN_TITLES = ["Company", "Services", "Resources", "Quick Links", "Explore", "Support"];
const CUSTOM_COLUMN_TITLE = "__custom_column__";

function normalizeAdminContactIcon(icon) {
  const raw = String(icon || "").trim().toLowerCase().replace(/\s+/g, "");
  const map = {
    loc: "LOC", location: "LOC", office: "LOC", address: "LOC",
    mail: "MAIL", email: "MAIL", "e-mail": "MAIL",
    call: "CALL", phone: "CALL", tel: "CALL", mobile: "CALL",
    web: "WEB", website: "WEB", url: "WEB", site: "WEB",
  };
  if (map[raw]) return map[raw];
  const upper = String(icon || "").toUpperCase();
  if (["LOC", "MAIL", "CALL", "WEB"].includes(upper)) return upper;
  return "MAIL";
}

function contactLabelForAdmin(icon) {
  const key = normalizeAdminContactIcon(icon);
  const hit = CONTACT_TYPE_OPTIONS.find((o) => o.value === key);
  return hit ? hit.label : "Contact";
}

function FooterPanel() {
  const { data, setData, loading, dirty, markClean } = useSiteSection("footer");
  const [saving, setSaving] = useState(false);
  const [routes, setRoutes] = useState([]);
  const [loadingRoutes, setLoadingRoutes] = useState(true);
  const footerFallback = siteStaticUrl("footerLogo");
  const footerLogo = resolveMediaUrl(data.media?.src, footerFallback) || footerFallback;

  const fetchRoutes = async () => {
    setLoadingRoutes(true);
    try {
      const { data: payload } = await api.get("/navigation/routes");
      setRoutes(payload?.routes || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load public routes");
      setRoutes([]);
    } finally {
      setLoadingRoutes(false);
    }
  };

  useEffect(() => { fetchRoutes(); }, []);

  const columnTitleOptions = useMemo(() => {
    const seen = new Set(FOOTER_COLUMN_TITLES.map((t) => t.toLowerCase()));
    const extras = [];
    for (const col of data.columns || []) {
      const t = (col.title || "").trim();
      if (!t || seen.has(t.toLowerCase())) continue;
      seen.add(t.toLowerCase());
      extras.push(t);
    }
    return [...FOOTER_COLUMN_TITLES, ...extras];
  }, [data.columns]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...data,
        contact: (data.contact || []).map((row) => ({
          ...row,
          icon: normalizeAdminContactIcon(row.icon),
        })),
      };
      await api.patch("/admin/site/footer", payload);
      markClean(payload);
      setData(payload);
      toast.success("Footer saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving(false);
  };

  const contactRows = (data.contact || []).map((c) => ({
    ...c,
    icon: normalizeAdminContactIcon(c.icon),
  }));

  return (
    <StudioShell
      title="Footer"
      description="Column links use the same live public routes as Navigation (pages, services, blog, legal). Pick a name and endpoint — or enter a custom / https link."
      dirty={dirty} saving={saving} onSave={save} loading={loading}
      preview={(
        <div
          className="rounded-2xl overflow-hidden border border-white/10"
          style={{ background: "linear-gradient(160deg, #071628 0%, #0a2540 55%, #0d2f4f 100%)" }}
        >
          <div className="p-5 space-y-5">
            <div className="flex items-start gap-3">
              {footerLogo ? (
                <img src={footerLogo} alt="" className="w-10 h-10 object-contain rounded-lg bg-white p-1 shrink-0" />
              ) : null}
              <p className="text-[12px] leading-relaxed text-sky-100/70 line-clamp-3">
                {data.description || "Footer description"}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {(data.columns || []).slice(0, 2).map((c, i) => (
                <div key={i}>
                  <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-white mb-2">
                    {c.title || "Column"}
                  </div>
                  <ul className="space-y-1.5">
                    {(c.links || []).slice(0, 4).map((l, j) => (
                      <li key={j} className="text-[11px] text-white/55">
                        {l.label || "Link"}
                        {l.href ? <span className="text-white/30 ml-1 font-mono">{l.href}</span> : null}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {contactRows.length > 0 && (
              <div>
                <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-white mb-2">Contact</div>
                <div className="space-y-2.5">
                  {contactRows.slice(0, 4).map((c, j) => (
                    <div key={j}>
                      <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-sky-300/80">
                        {contactLabelForAdmin(c.icon)}
                      </div>
                      <div className={`text-[12px] text-white ${normalizeAdminContactIcon(c.icon) === "MAIL" ? "font-medium" : ""}`}>
                        {c.text || "—"}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(data.socials || []).length > 0 && (
              <div className="flex flex-wrap gap-2">
                {(data.socials || []).map((s, i) => (
                  <span key={i} className="px-2 py-1 rounded-full text-[10px] border border-sky-300/25 text-sky-100/80">
                    {s.platform || "Social"}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="px-5 py-3 border-t border-white/10 text-[11px] text-white/40">
            {data.copyright || "© Copyright"}
          </div>
        </div>
      )}
    >
      <MediaImageField
        label="Footer logo"
        value={data.media || null}
        onChange={(media) => setData({ ...data, media })}
        folder="brand"
        valueMode="media"
        aspectClass="h-20"
        fallbackSrc={footerFallback}
      />
      <Field label="Description"><Textarea value={data.description || ""} onChange={(e) => setData({ ...data, description: e.target.value })} rows={2} className={inputClass} /></Field>
      <Field label="Copyright"><Input value={data.copyright || ""} onChange={(e) => setData({ ...data, copyright: e.target.value })} className={inputClass} /></Field>
      <Field label="Legal links" hint="Pipe-separated"><Input value={data.legal || ""} onChange={(e) => setData({ ...data, legal: e.target.value })} className={inputClass} /></Field>

      <Field label="Social links" hint="Drag to reorder">
        <DragList
          items={data.socials || []}
          onChange={(socials) => setData({ ...data, socials })}
          emptyText="No socials"
          addLabel="Add social"
          onAdd={() => setData({ ...data, socials: [...(data.socials || []), { platform: "", url: "" }] })}
          renderItem={(s, _i, { update }) => (
            <div className="space-y-1.5">
              <Input value={s.platform || ""} onChange={(e) => update({ platform: e.target.value })} placeholder="Platform" className={`${inputClass} text-xs h-8`} />
              <Input value={s.url || ""} onChange={(e) => update({ url: e.target.value })} placeholder="https://…" className={`${inputClass} text-xs h-8`} />
            </div>
          )}
        />
      </Field>

      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.14em] text-white/45">Footer columns</div>
        <Button
          type="button"
          variant="ghost"
          onClick={fetchRoutes}
          disabled={loadingRoutes}
          className="h-7 px-2 text-[11px] text-white/60 hover:text-white hover:bg-white/5"
        >
          <Icons.RefreshCw className={`w-3 h-3 mr-1 ${loadingRoutes ? "animate-spin" : ""}`} />
          {loadingRoutes ? "Loading routes…" : `${routes.length} routes`}
        </Button>
      </div>
      <Field label="Columns" hint="Column title + dynamic link name / endpoint from live public routes.">
        <DragList
          items={data.columns || []}
          onChange={(columns) => setData({ ...data, columns })}
          emptyText="No columns"
          addLabel="Add column"
          onAdd={() => setData({
            ...data,
            columns: [...(data.columns || []), { title: "Company", links: [{ label: "About", href: "/about" }] }],
          })}
          renderItem={(column, _colIdx, { update }) => {
            const titleKnown = columnTitleOptions.some((t) => t === (column.title || ""));
            const titleSelect = titleKnown ? column.title : CUSTOM_COLUMN_TITLE;
            return (
              <div className="space-y-2">
                <Select
                  value={titleSelect || CUSTOM_COLUMN_TITLE}
                  onValueChange={(v) => {
                    if (v === CUSTOM_COLUMN_TITLE) update({ title: "" });
                    else update({ title: v });
                  }}
                >
                  <SelectTrigger className={`${inputClass} text-xs h-8`}>
                    <SelectValue placeholder="Column title" />
                  </SelectTrigger>
                  <SelectContent className="glass-strong border-white/10 text-white">
                    {columnTitleOptions.map((t) => (
                      <SelectItem key={t} value={t} className="text-white focus:bg-white/10 focus:text-white">
                        {t}
                      </SelectItem>
                    ))}
                    <SelectItem value={CUSTOM_COLUMN_TITLE} className="text-white focus:bg-white/10 focus:text-white">
                      Custom title…
                    </SelectItem>
                  </SelectContent>
                </Select>
                {titleSelect === CUSTOM_COLUMN_TITLE && (
                  <Input
                    value={column.title || ""}
                    onChange={(e) => update({ title: e.target.value })}
                    placeholder="Custom column title"
                    className={`${inputClass} text-xs h-8`}
                  />
                )}
                <DragList
                  items={column.links || []}
                  onChange={(links) => update({ links })}
                  emptyText="No links"
                  addLabel="Add link"
                  onAdd={() => {
                    const home = routes.find((r) => r.path === "/") || { label: "Home", path: "/" };
                    update({
                      links: [...(column.links || []), { label: home.label, href: home.path }],
                    });
                  }}
                  renderItem={(link, _li, helpers) => (
                    <PublicRouteLinkFields
                      compact
                      routes={routes}
                      value={link}
                      hrefKey="href"
                      onChange={(next) => helpers.update(next)}
                    />
                  )}
                />
              </div>
            );
          }}
        />
      </Field>

      <Field label="Contact rows" hint="Type controls the public label: Email / Phone / Office / Website.">
        <DragList
          items={data.contact || []}
          onChange={(contact) => setData({ ...data, contact })}
          emptyText="No contact rows"
          addLabel="Add row"
          onAdd={() => setData({ ...data, contact: [...(data.contact || []), { icon: "MAIL", text: "" }] })}
          renderItem={(item, _i, { update }) => (
            <div className="grid grid-cols-[110px_1fr] gap-1.5">
              <select
                value={normalizeAdminContactIcon(item.icon)}
                onChange={(e) => update({ icon: e.target.value })}
                className={`${inputClass} text-xs h-8 rounded-md px-2`}
              >
                {CONTACT_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value} className="bg-slate-900 text-white">
                    {opt.label}
                  </option>
                ))}
              </select>
              <Input value={item.text || ""} onChange={(e) => update({ text: e.target.value })} placeholder="Detail" className={`${inputClass} text-xs h-8`} />
            </div>
          )}
        />
      </Field>
    </StudioShell>
  );
}

const PANEL_MAP = {
  brand: BrandPanel,
  hero: HeroPanel,
  about: AboutPanel,
  "services-header": ServicesHeaderPanel,
  "section-headers": SectionHeadersPanel,
  cta: CtaPanel,
  pricing: PricingPanel,
  careers: CareersPanel,
  contact: ContactPanel,
  footer: FooterPanel,
};

export default function SiteSettings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const panelParam = searchParams.get("panel") || "brand";
  const sectionParam = searchParams.get("section") || "";
  const [active, setActive] = useState(PANEL_MAP[panelParam] ? panelParam : "brand");

  useEffect(() => {
    if (PANEL_MAP[panelParam]) setActive(panelParam);
  }, [panelParam]);

  const selectPanel = (key) => {
    setActive(key);
    const next = new URLSearchParams(searchParams);
    next.set("panel", key);
    if (key !== "section-headers") next.delete("section");
    setSearchParams(next, { replace: true });
  };

  const Active = PANEL_MAP[active] || BrandPanel;
  const activeProps = active === "section-headers" ? { initialSection: sectionParam || "why_choose" } : {};

  return (
    <div>
      <PageHeader
        title="Brand & Pages"
        subtitle="Studio editor for brand identity and site-wide page copy. Drag list items to reorder — preview updates live."
      />
      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-4 items-start">
        <GlassCard className="p-2 lg:sticky lg:top-20">
          <div className="px-2 pt-2 pb-3">
            <div className="text-[10px] uppercase tracking-[0.16em] text-white/35">Content studio</div>
          </div>
          <nav className="space-y-0.5">
            {PANELS.map((p) => {
              const I = Icons[p.icon] || Icons.Circle;
              const on = active === p.key;
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => selectPanel(p.key)}
                  className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-left transition-colors ${
                    on
                      ? "bg-white/[0.1] text-white shadow-[inset_0_0_0_1px_rgba(34,211,238,0.25)]"
                      : "text-white/60 hover:bg-white/[0.04] hover:text-white/90"
                  }`}
                >
                  <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${on ? "bg-cyan-400/15 text-cyan-100" : "bg-white/[0.04] text-white/50"}`}>
                    <I className="w-4 h-4" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium truncate">{p.label}</span>
                    <span className="block text-[10px] text-white/35 truncate">{p.blurb}</span>
                  </span>
                </button>
              );
            })}
          </nav>
        </GlassCard>
        <div className="min-w-0">
          <Active {...activeProps} />
        </div>
      </div>
    </div>
  );
}
